import { Action, Dispatch, Middleware } from 'redux';
import { isAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import { pushComponentDesignUndoState, redoComponentDesign, undoComponentDesign } from '../slices/editorSlice';

const IGNORE_FLAG = '__IGNORE_FOR_UNDO_REDO_SNAPSHOTS__';

export interface UndoRedoIgnorableAction extends Action {
  [IGNORE_FLAG]?: true;
}

/**
 * Call this function on an action's payload to ensure an undo snapshot will not be created, even if it causes a
 *   component design to change.
 */
export function withoutCreatingUndoSnapshot<T>(payload: T) {
  return {
    ...payload,
    [IGNORE_FLAG]: true as const,
  };
}

interface ActionWithPayload extends Action {
  payload: unknown;
}

function isActionWithPayload(action: Action): action is ActionWithPayload {
  return 'payload' in action;
}

function isUndoRedoIgnorableAction(action: Action): action is UndoRedoIgnorableAction {
  return (
    isActionWithPayload(action) &&
    !!action.payload &&
    typeof action.payload === 'object' &&
    IGNORE_FLAG in action.payload
  );
}

export const editorMiddleware: Middleware<{}, RootState, Dispatch<Action>> = (store) => (next) => (action) => {
  if (!isAction(action)) {
    return next(action);
  }

  const oldState = store.getState();
  next(action);
  if (undoComponentDesign.match(action) || redoComponentDesign.match(action)) {
    return;
  }
  const newState = store.getState();
  if (newState.editor.editInProgress || oldState.componentDesigns.data === newState.componentDesigns.data) {
    return;
  }
  for (const newComponentDesign of Object.values(newState.componentDesigns.data)) {
    const oldComponentDesign = oldState.componentDesigns.data[newComponentDesign.guid];

    if (oldComponentDesign && oldComponentDesign !== newComponentDesign && !isUndoRedoIgnorableAction(action)) {
      store.dispatch(pushComponentDesignUndoState(oldComponentDesign));
    }
  }
};
